import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { travelTools } from '../../../../mocks/travel-tools';
import { travelReviews } from '../../../../mocks/travel-reviews';
import PageMeta from '../../../../components/base/PageMeta';
import JsonLd from '../../../../components/base/JsonLd';
import BrandLogo from '../../../../components/base/BrandLogo';
import { getReviewAvatar } from '../../../../utils/reviewAvatar';

export default function TravelLifestyleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'details' | 'pricing'>('overview');
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [helpfulVotes, setHelpfulVotes] = useState<Record<number, boolean>>({});
  const [saved, setSaved] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const tool = travelTools.find(t => t.slug === slug);
  const reviews = travelReviews[slug ?? ''] ?? [];
  const relatedTools = travelTools.filter(t => tool?.relatedSlugs.includes(t.slug));
  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';
  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const renderStars = (r: number) =>
    Array.from({ length: 5 }).map((_, i) => {
      if (i < Math.floor(r)) return <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>;
      if (i === Math.floor(r) && r % 1 >= 0.5) return <i key={i} className="ri-star-half-line text-amber-400 text-sm"></i>;
      return <i key={i} className="ri-star-line text-gray-300 text-sm"></i>;
    });

  if (!tool) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f0fdfa' }}>
        <div className="text-center">
          <div className="text-5xl font-black text-gray-200 mb-4">404</div>
          <p className="text-gray-500 mb-4">Platform not found</p>
          <a href="/categories/travel-lifestyle" className="text-teal-700 font-semibold hover:underline cursor-pointer">← Back to Travel – Lifestyle Services</a>
        </div>
      </div>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Review',
        name: `${tool.name} Review 2026 – ${tool.tagline}`,
        url: `${siteUrl}/categories/travel-lifestyle/${tool.slug}`,
        itemReviewed: { '@type': 'Organization', name: tool.name, description: tool.tagline },
        reviewRating: { '@type': 'Rating', ratingValue: tool.rating, bestRating: 5 },
        author: { '@type': 'Organization', name: '247digitrend', url: siteUrl },
        publisher: { '@type': 'Organization', name: '247digitrend', url: siteUrl },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/categories` },
          { '@type': 'ListItem', position: 3, name: 'Travel – Lifestyle Services', item: `${siteUrl}/categories/travel-lifestyle` },
          { '@type': 'ListItem', position: 4, name: tool.name, item: `${siteUrl}/categories/travel-lifestyle/${tool.slug}` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen" style={{ background: '#f0fdfa', fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title={`${tool.name} Review 2026 – ${tool.tagline} | 247digitrend`}
        description={`${tool.intro[0].substring(0, 155)}...`}
        canonical={`${siteUrl}/categories/travel-lifestyle/${tool.slug}`}
        keywords={`${tool.name} review, travel lifestyle platform, ${tool.quickInfo.category.toLowerCase()} review 2026`}
        ogType="article"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={e => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer"><i className="ri-close-line text-gray-600"></i></button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-teal-100"><i className="ri-check-line text-teal-600 text-2xl"></i></div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold py-3 rounded-full cursor-pointer whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500"><i className="ri-plane-line text-white text-base"></i></div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247digitrend</p>
                </div>
                <form onSubmit={e => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={e => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={e => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-teal-400" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white/90 border-b border-teal-200'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[15px] font-medium text-gray-700 hover:text-teal-700 cursor-pointer">Home</a>
            <a href="/categories" className="text-[15px] font-semibold text-teal-700 cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[15px] font-medium text-gray-700 hover:text-teal-700 cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[15px] font-medium text-gray-700 hover:text-teal-700 cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[15px] font-medium text-gray-700 hover:text-teal-700 cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map(lang => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold cursor-pointer ${currentLang === lang ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span><span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90">Sign Up</button>
          </div>
        </div>
      </header>

      <div className="pt-36 bg-white border-b border-teal-100">
        <div className="max-w-6xl mx-auto px-8 py-3 flex items-center gap-2 text-xs text-gray-500">
          <a href="/" className="hover:text-teal-700 cursor-pointer">Home</a>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <a href="/categories" className="hover:text-teal-700 cursor-pointer">Categories</a>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <a href="/categories/travel-lifestyle" className="hover:text-teal-700 cursor-pointer">Travel – Lifestyle Services</a>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-gray-800 font-semibold truncate max-w-40">{tool.name}</span>
        </div>
      </div>

      <div className="bg-white border-b border-teal-100">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <div className="flex items-start gap-5">
            <BrandLogo logoUrl={tool.logoUrl} logoGrad={tool.logoGrad} logoIcon={tool.logoIcon} name={tool.name} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h1 className="text-2xl font-black text-gray-900">{tool.name}</h1>
                <span className="text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-0.5">{tool.priceShort}</span>
                {tool.badge && <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${tool.badgeColor}`}>{tool.badge}</span>}
              </div>
              <p className="text-sm text-gray-500 mb-3">{tool.tagline}</p>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-0.5">{renderStars(tool.rating)}</div>
                  <span className="text-sm font-bold text-gray-800">{tool.rating}</span>
                  <span className="text-xs text-gray-400">({tool.reviewCount.toLocaleString()} reviews)</span>
                </div>
                <span className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-3 py-1"><i className="ri-price-tag-3-line mr-1"></i>{tool.price}</span>
                <span className="text-xs font-semibold text-teal-700 bg-teal-50 rounded-full px-3 py-1"><i className="ri-plane-line mr-1"></i>{tool.quickInfo.category}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-500 hover:opacity-90 text-white text-sm font-semibold px-5 py-2.5 rounded-xl cursor-pointer transition-all whitespace-nowrap">
                <i className="ri-external-link-line text-sm"></i>Visit Platform
              </a>
              <button onClick={() => setSaved(!saved)} className={`flex items-center gap-2 border text-sm font-semibold px-4 py-2.5 rounded-xl cursor-pointer transition-all whitespace-nowrap ${saved ? 'border-teal-300 text-teal-700 bg-teal-50' : 'border-gray-200 hover:border-teal-300 text-gray-600 hover:text-teal-700'}`}>
                <i className={`${saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-sm`}></i>{saved ? 'Saved' : 'Save'}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-0 mt-6 border-b border-teal-100">
            {(['overview', 'details', 'pricing'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`px-5 py-2.5 text-sm font-semibold capitalize transition-all cursor-pointer border-b-2 -mb-px ${activeTab === tab ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                {tab === 'details' ? 'Key Details' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-8 py-10">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-8">
            {activeTab === 'overview' && (
              <>
                <div className="w-full h-64 rounded-2xl overflow-hidden mb-8">
                  <img src={tool.image} alt={tool.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="mb-8">
                  <h2 className="text-lg font-black text-gray-900 mb-4">About {tool.name}</h2>
                  <div className="space-y-4">{tool.intro.map((p, i) => (<p key={i} className="text-sm text-gray-600 leading-relaxed">{p}</p>))}</div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-teal-50 rounded-2xl p-5 border border-teal-200">
                    <h3 className="text-sm font-black text-teal-800 mb-3 flex items-center gap-2"><i className="ri-thumb-up-line text-teal-600"></i>Pros</h3>
                    <ul className="space-y-2">{tool.pros.map((p, i) => (<li key={i} className="flex items-start gap-2 text-xs text-teal-700"><i className="ri-check-line text-teal-500 text-sm mt-0.5 flex-shrink-0"></i><span>{p}</span></li>))}</ul>
                  </div>
                  <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
                    <h3 className="text-sm font-black text-red-800 mb-3 flex items-center gap-2"><i className="ri-thumb-down-line text-red-500"></i>Cons</h3>
                    <ul className="space-y-2">{tool.cons.map((c, i) => (<li key={i} className="flex items-start gap-2 text-xs text-red-700"><i className="ri-close-line text-red-400 text-sm mt-0.5 flex-shrink-0"></i><span>{c}</span></li>))}</ul>
                  </div>
                </div>
                <div className="bg-teal-50 rounded-2xl p-5 border border-teal-200 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-100 flex-shrink-0"><i className="ri-information-line text-teal-600 text-sm"></i></div>
                    <div>
                      <h4 className="text-sm font-black text-teal-900 mb-1">Affiliate Disclosure</h4>
                      <p className="text-xs text-teal-700 leading-relaxed">This platform is an affiliate partner of 247digitrend. We may earn a commission when you book or purchase through our links — at no extra cost to you. Our editorial reviews remain independent and unbiased. We only feature platforms we genuinely believe provide value to travelers.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-lg font-black text-gray-900">Traveller Reviews</h2>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5">{renderStars(tool.rating)}</div>
                      <span className="text-sm font-bold text-gray-800">{tool.rating}/5</span>
                      <span className="text-xs text-gray-400">({tool.reviewCount.toLocaleString()})</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {reviews.map((r, i) => (
                      <div key={i} className="bg-white rounded-2xl border border-teal-100 p-5">
                        <div className="flex items-start gap-3 mb-3">
                          <img src={getReviewAvatar(r.name)} alt={r.name} className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0" />
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-gray-900">{r.name}</span>
                              {r.badges.map((b, bi) => (
                                <span key={bi} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${b === 'Verified' ? 'bg-teal-100 text-teal-700' : b === 'Frequent Traveller' ? 'bg-cyan-100 text-cyan-700' : b === 'Budget Traveller' ? 'bg-lime-100 text-lime-700' : b === 'Business Traveller' ? 'bg-amber-100 text-amber-700' : b === 'Digital Nomad' ? 'bg-emerald-100 text-emerald-700' : b === 'Travel Blogger' ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-700'}`}>{b}</span>
                              ))}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <div className="flex items-center gap-0.5">{renderStars(r.rating)}</div>
                              <span className="text-xs text-gray-400">{r.date}</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed mb-3">{r.text}</p>
                        <div className="flex items-center gap-4">
                          <button onClick={() => setHelpfulVotes(p => ({ ...p, [i]: !p[i] }))} className={`flex items-center gap-1.5 text-xs font-semibold cursor-pointer transition-colors ${helpfulVotes[i] ? 'text-teal-700' : 'text-gray-400 hover:text-gray-700'}`}>
                            <i className="ri-thumb-up-line text-sm"></i>Helpful ({helpfulVotes[i] ? r.helpful + 1 : r.helpful})
                          </button>
                          <button className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer">Report</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 bg-white rounded-2xl p-5 border border-teal-100">
                    <h3 className="text-sm font-black text-gray-800 mb-3">About These Reviews</h3>
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">Reviews aggregated from verified travellers across multiple travel communities and booking platforms. Individual experiences vary.</p>
                    <div className="flex items-center gap-3">{['Trustpilot', 'TripAdvisor', 'Google Reviews', 'Platform'].map((s, i) => (<span key={i} className="text-xs font-semibold text-gray-500 bg-white border border-teal-100 rounded-lg px-2.5 py-1">{s}</span>))}</div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'details' && (
              <div>
                <div className="w-full h-64 rounded-2xl overflow-hidden mb-8"><img src={tool.image} alt={tool.name} className="w-full h-full object-cover object-top" /></div>
                <h2 className="text-lg font-black text-gray-900 mb-6">Key Features &amp; Details</h2>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {tool.pros.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-teal-100">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${tool.logoGrad} flex-shrink-0`}><i className={`${tool.logoIcon} text-white text-sm`}></i></div>
                      <p className="text-xs text-gray-700 leading-relaxed">{feature}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-teal-50 rounded-2xl p-6 border border-teal-200 mb-4">
                  <h3 className="text-sm font-black text-teal-900 mb-2 flex items-center gap-2"><i className="ri-information-line text-teal-600"></i>Affiliate Disclosure</h3>
                  <p className="text-xs text-teal-800 leading-relaxed mb-3">This platform is an affiliate partner of 247digitrend. We earn a commission on bookings through our links at no extra cost to you. Reviews remain unbiased and independent.</p>
                  <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:text-teal-900 cursor-pointer">View full details on {tool.name} <i className="ri-external-link-line text-xs"></i></a>
                </div>
              </div>
            )}

            {activeTab === 'pricing' && (
              <div>
                <h2 className="text-lg font-black text-gray-900 mb-2">Pricing Information</h2>
                <p className="text-sm text-gray-500 mb-6">{tool.quickInfo.pricing}</p>
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[
                    { tier: 'Explorer', desc: 'Single booking or free registration to discover the platform and explore available options', badge: '', btnClass: 'bg-gray-900 text-white hover:bg-gray-700' },
                    { tier: 'Traveller', desc: 'Most popular: bundle bookings or subscription plans offering the best overall travel value', badge: 'Best Value', btnClass: 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:opacity-90' },
                    { tier: 'Frequent Flyer', desc: 'Premium plan for frequent travellers seeking maximum savings and priority access', badge: '', btnClass: 'bg-gray-900 text-white hover:bg-gray-700' },
                  ].map((plan, i) => (
                    <div key={i} className={`rounded-2xl border-2 p-5 relative ${i === 1 ? 'border-teal-400 bg-teal-50' : 'border-gray-200 bg-white'}`}>
                      {plan.badge && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-bold bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-3 py-0.5 rounded-full whitespace-nowrap">{plan.badge}</span>}
                      <h3 className="text-sm font-black text-gray-900 mb-2">{plan.tier}</h3>
                      <p className="text-xs text-gray-500 leading-relaxed mb-4">{plan.desc}</p>
                      <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className={`flex items-center justify-center gap-1.5 w-full text-xs font-semibold py-2.5 rounded-xl cursor-pointer transition-all whitespace-nowrap ${plan.btnClass}`}>Book Now <i className="ri-external-link-line text-xs"></i></a>
                    </div>
                  ))}
                </div>
                <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                  <p className="text-xs text-teal-800 flex items-start gap-2"><i className="ri-information-line text-teal-600 text-sm mt-0.5 flex-shrink-0"></i>Prices are subject to change with seasons and availability. Visit {tool.name}&#39;s official platform for current pricing and promotions.</p>
                </div>
              </div>
            )}
          </div>

          <div className="col-span-4">
            <div className="bg-white rounded-2xl border border-teal-100 p-5 sticky top-32">
              <h3 className="text-sm font-black text-gray-800 mb-4">Quick Info</h3>
              <div className="space-y-3">
                {[
                  { label: 'Company', value: tool.quickInfo.company, icon: 'ri-building-line' },
                  { label: 'Category', value: tool.quickInfo.category, icon: 'ri-plane-line' },
                  { label: 'Rating', value: `${tool.rating}/5.0 (${tool.reviewCount.toLocaleString()} reviews)`, icon: 'ri-star-line' },
                  { label: 'Travellers', value: tool.quickInfo.users, icon: 'ri-group-line' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-teal-50 flex-shrink-0"><i className={`${item.icon} text-teal-600 text-xs`}></i></div>
                    <div>
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-xs font-semibold text-gray-800">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className={`mt-5 rounded-xl p-4 bg-gradient-to-br ${tool.logoGrad}`}>
                <p className="text-xs font-black text-white mb-1">Book with {tool.name}</p>
                <p className="text-xs text-white/80 mb-3">Join millions of travellers who trust this platform for their travel needs.</p>
                <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center justify-center gap-1.5 w-full bg-white text-gray-900 text-xs font-bold py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">Visit {tool.name} <i className="ri-external-link-line text-xs"></i></a>
              </div>
              <div className="mt-4 bg-teal-50 rounded-xl p-4 border border-teal-200">
                <div className="flex items-center gap-2 mb-2"><i className="ri-shield-check-line text-teal-600 text-sm"></i><span className="text-xs font-black text-teal-800">Affiliate Disclosure</span></div>
                <p className="text-xs text-teal-700 leading-relaxed">Links on this page may be affiliate links. We earn a small commission at no extra cost to you.</p>
              </div>
            </div>
          </div>
        </div>

        {relatedTools.length > 0 && (
          <div className="mt-12 pt-10 border-t border-teal-100">
            <h2 className="text-lg font-black text-gray-900 mb-6">Related platforms</h2>
            <div className="grid grid-cols-3 gap-5">
              {relatedTools.map((rt, i) => (
                <a key={i} href={`/categories/travel-lifestyle/${rt.slug}`} className="group flex items-start gap-3 bg-white rounded-2xl border border-teal-100 p-4 hover:border-teal-400 transition-all cursor-pointer">
                  <BrandLogo logoUrl={rt.logoUrl} logoGrad={rt.logoGrad} logoIcon={rt.logoIcon} name={rt.name} size="sm" />
                  <div className="min-w-0">
                    <h4 className="text-sm font-black text-gray-900 group-hover:text-teal-700 transition-colors">{rt.name}</h4>
                    <p className="text-xs text-gray-400 truncate">{rt.tagline}</p>
                    <div className="flex items-center gap-1 mt-1"><i className="ri-star-fill text-amber-400 text-xs"></i><span className="text-xs font-semibold text-gray-600">{rt.rating}</span><span className="text-xs text-gray-400 ml-1 whitespace-nowrap">View details</span></div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer style={{ background: '#f0fdfa' }} className="pt-14 pb-6 mt-10 border-t border-teal-200">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-10 pb-10 border-b border-teal-100">
            <div className="md:col-span-4">
              <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain mb-4" />
              <p className="text-sm text-gray-500 leading-relaxed">247digitrend provides unbiased reviews of travel and lifestyle platforms including tours, eSIM providers, group travel, and affiliate networks.</p>
            </div>
            <div className="md:col-span-2 md:col-start-6">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Resources</h5>
              {[{ label: 'All Reviews', href: '/reviews' }, { label: 'Comparisons', href: '/comparisons' }, { label: 'Categories', href: '/categories' }, { label: 'Blog', href: '/blog' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-teal-700 mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Company</h5>
              {[{ label: 'About Us', href: '/about' }, { label: 'Contact', href: '/contact' }, { label: 'Submit a Tool', href: '/submit-tool' }, { label: 'Advertise', href: '/advertise' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-teal-700 mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Follow Us</h5>
              <div className="flex items-center gap-3">
                {['ri-twitter-x-line', 'ri-linkedin-line', 'ri-facebook-line', 'ri-instagram-line'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-teal-200 bg-white text-gray-500 hover:text-teal-700 hover:border-teal-400 cursor-pointer"><i className={`${icon} text-base`}></i></a>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-400">© 2026 247digitrend. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-gray-400 hover:text-teal-700 cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-xs text-gray-400 hover:text-teal-700 cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-teal-700 cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
